import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useIndividualsApi } from '@/hooks/useIndividualsApi';
import { formatDateShort } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { useQuery } from '@tanstack/react-query';
import { Globe, Info, Loader2, Medal, Trophy } from 'lucide-react';
import { useMemo, useState } from 'react';

export default function IndividualLeaderboard() {
  const individualsApi = useIndividualsApi();
  const { user } = useAuthStore();
  const [selectedWorkoutId, setSelectedWorkoutId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [ageGroupFilter, setAgeGroupFilter] = useState('');

  // Fetch recent workouts (no date filter) so the picker is populated even on
  // days without scheduled programming. FEAT-APR07-011.
  const { data: workoutsData } = useQuery({
    queryKey: ['individual-workouts-all'],
    queryFn: async () => {
      const response = await individualsApi.getWorkouts();
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  // FEAT-APR30-002 — show scheduled date alongside title to disambiguate
  // recurring workout names (e.g. "Daily WOD – AMRAP" appears multiple times).
  const workoutOptions = useMemo(() => {
    const workouts = workoutsData?.workouts || [];
    return workouts
      .filter((w) => w.workout_date)
      .map((w) => ({
        value: w.id,
        label: `${w.name} (${formatDateShort(w.workout_date)})`,
      }));
  }, [workoutsData?.workouts]);

  const effectiveWorkoutId = selectedWorkoutId || workoutOptions[0]?.value || '';

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'individual-leaderboard',
      effectiveWorkoutId,
      searchQuery,
      countryFilter,
      genderFilter,
      ageGroupFilter,
    ],
    queryFn: async () => {
      if (!effectiveWorkoutId) return { leaderboard: [], workout: null, filters: {} };
      const response = await individualsApi.getLeaderboard(effectiveWorkoutId, {
        country: countryFilter || undefined,
        gender: genderFilter || undefined,
        age_group: ageGroupFilter || undefined,
        search: searchQuery || undefined,
      });
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    enabled: !!effectiveWorkoutId,
    refetchInterval: 30000,
  });

  const hasActiveFilters = !!(searchQuery || countryFilter || genderFilter || ageGroupFilter);

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'text-yellow-500';
      case 2:
        return 'text-gray-400';
      case 3:
        return 'text-amber-600';
      default:
        return 'text-muted-foreground';
    }
  };

  const getRankIcon = (rank: number) => {
    if (rank <= 3) {
      return <Trophy className={`h-5 w-5 ${getRankColor(rank)}`} />;
    }
    return <span className="w-5 text-center font-medium">{rank}</span>;
  };

  const formatScore = (value: number, unit: string) => {
    if (unit === 'time' || unit === 'seconds') {
      const mins = Math.floor(value / 60);
      const secs = value % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${value} ${unit}`;
  };

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
        <p className="text-destructive">Failed to load leaderboard</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">Leaderboard</h1>
        <p className="mt-1 text-muted-foreground">See how you stack up against other athletes.</p>
      </div>

      {/* Coming Soon Banner */}
      <Alert className="mb-6">
        <Info className="h-4 w-4" />
        <AlertTitle>Global Leaderboard</AlertTitle>
        <AlertDescription>
          You're viewing the global leaderboard. Enhanced individual rankings and personal records
          tracking are coming soon!
        </AlertDescription>
      </Alert>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="flex-1">
                <label htmlFor="workout-select" className="text-sm font-medium mb-2 block">
                  Workout:
                </label>
                <Select
                  id="workout-select"
                  options={[{ value: '', label: 'Select a track...' }, ...workoutOptions]}
                  value={selectedWorkoutId || effectiveWorkoutId}
                  onChange={(e) => setSelectedWorkoutId(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <Badge variant="secondary" className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  {hasActiveFilters ? 'Filtered' : 'Global'} Rankings
                </Badge>
              </div>
            </div>
            <div>
              <label
                htmlFor="search-input"
                className="text-xs font-medium mb-1 block text-muted-foreground"
              >
                Search Athlete
              </label>
              <input
                id="search-input"
                type="text"
                placeholder="Search by name..."
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div>
                <label
                  htmlFor="country-filter"
                  className="text-xs font-medium mb-1 block text-muted-foreground"
                >
                  Country
                </label>
                <select
                  id="country-filter"
                  className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="GB">GB</option>
                  <option value="US">US</option>
                  <option value="AU">AU</option>
                  <option value="CA">CA</option>
                  <option value="NZ">NZ</option>
                  <option value="IE">IE</option>
                  <option value="ZA">ZA</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="gender-filter"
                  className="text-xs font-medium mb-1 block text-muted-foreground"
                >
                  Gender
                </label>
                <select
                  id="gender-filter"
                  className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                  value={genderFilter}
                  onChange={(e) => setGenderFilter(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="age-filter"
                  className="text-xs font-medium mb-1 block text-muted-foreground"
                >
                  Age Group
                </label>
                <select
                  id="age-filter"
                  className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                  value={ageGroupFilter}
                  onChange={(e) => setAgeGroupFilter(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="18-29">18–29</option>
                  <option value="30-39">30–39</option>
                  <option value="40-49">40–49</option>
                  <option value="50+">50+</option>
                </select>
              </div>
              {hasActiveFilters && (
                <div className="flex items-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs"
                    onClick={() => {
                      setSearchQuery('');
                      setCountryFilter('');
                      setGenderFilter('');
                      setAgeGroupFilter('');
                    }}
                  >
                    Clear filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leaderboard Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Medal className="h-5 w-5 text-primary" />
            Rankings
            <Badge variant="outline" className="ml-2">
              {hasActiveFilters ? 'Filtered' : 'All Athletes'}
            </Badge>
            <Badge variant="secondary" className="ml-auto">
              Auto-refreshes every 30s
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!data?.leaderboard || data.leaderboard.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Medal className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">No scores yet</h3>
              <p className="text-muted-foreground max-w-md">
                {effectiveWorkoutId
                  ? 'No one has logged scores for this workout yet. Be the first!'
                  : 'Select a track to view the leaderboard.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Rank</TableHead>
                  <TableHead>Athlete</TableHead>
                  <TableHead>Gym</TableHead>
                  <TableHead className="text-right">Score</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.leaderboard.map((entry, index) => {
                  const isCurrentUser = entry.user_id === user?.id;
                  return (
                    <TableRow
                      key={entry.user_id}
                      className={`animate-fade-in ${
                        entry.rank <= 3 ? 'bg-primary/5' : ''
                      } ${isCurrentUser ? 'ring-2 ring-primary ring-inset' : ''}`}
                      style={{ animationDelay: `${index * 30}ms` }}
                    >
                      <TableCell>
                        <div className="flex items-center justify-center">
                          {getRankIcon(entry.rank)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`font-medium ${isCurrentUser ? 'text-primary' : ''}`}>
                          {entry.user_name}
                          {isCurrentUser && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              You
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {entry.affiliate_name || 'Individual'}
                      </TableCell>
                      <TableCell className="text-right">
                        <span
                          className={`font-mono font-semibold ${
                            entry.rank === 1 ? 'text-primary' : ''
                          }`}
                        >
                          {formatScore(entry.score_value, entry.score_unit)}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
